import { FC } from 'react';

import { Icon, Link, RyeconKey, Tooltip } from '@dbt-labs/sourdough';

import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useGenericResource } from '@/hooks/discovery/types';
import { useLinkToExplorer } from '@/hooks/links';
import { ProjectScope } from '@/hooks/params';
import { useTargetEnvironmentType } from '@/hooks/useTargetEnvironmentType';
import { parseUniqueId } from '@/typings/parseUniqueId';
import { PanelDescription } from '../../../../../components/ResourcePanel/ResourcePanels/Description';
import { ColumnPanelTabs } from './ColumnPanelTabs';

interface PanelDetailsProps {
  nodeUniqueId: string;
  columnName: string;
  projectId: number | undefined;
  environmentId: number | undefined;
}

export const ColumnPanelDetails: FC<PanelDetailsProps> = ({
  nodeUniqueId,
  columnName,
  projectId,
  environmentId = 0,
}) => {
  const { accountId, projectId: currentProjectId } = useDbtCloudProject();
  const { dbtCloudProjects } = useDbtCloudAccount();
  const getTargetEnvironmentType = useTargetEnvironmentType();
  const projectScope: ProjectScope = {
    accountId: accountId || 0,
    projectId: projectId || 0,
    environmentType: getTargetEnvironmentType(environmentId),
  };

  const linkToResource = useLinkToExplorer('resourceDetails');

  const parsedNodeUniqueId = parseUniqueId(nodeUniqueId);
  const resourceQuery = useGenericResource(parsedNodeUniqueId);
  const resource = resourceQuery.fetching ? null : resourceQuery.data;
  const column = resource?.catalog?.columns?.find((c) => c?.name === columnName);
  const project = dbtCloudProjects?.find((p) => p.id === projectId);
  const projectName = project?.name || parsedNodeUniqueId.packageName;
  const primaryKey = resource?.catalog?.primaryKey;

  return (
    <>
      <div className="w-full p-4">
        <div className="w-full overflow-hidden text-xl font-medium">
          <Tooltip displayOnlyWhenTruncated content={columnName}>
            {(ref) => (
              <div ref={ref} className="justify-items-center truncate">
                {columnName}{' '}
                {primaryKey?.includes(columnName.toLowerCase()) && (
                  <>
                    <Icon ryecon={RyeconKey} size="sm" />
                    {'  '}
                    <span className="text-base font-light">PK</span>
                  </>
                )}
              </div>
            )}
          </Tooltip>
        </div>
        <div className="mb-4 mt-1 text-xs font-normal">
          <Link
            isInternal={true}
            to={linkToResource({
              ...projectScope,
              dbtUniqueId: nodeUniqueId,
            })}
          >
            {/* XXX: should I use parseUniqueId or safeParseUniqueId */}
            {parsedNodeUniqueId.remainder}
          </Link>

          {projectId !== currentProjectId && (
            <span className="text-fgDecorative"> (Project: {projectName})</span>
          )}
        </div>
      </div>
      <ColumnPanelTabs>
        {(tabType) => {
          return (
            <>
              {tabType === 'general' && (
                <>
                  <div className="m-6">
                    <PanelDescription
                      descriptionOriginColumnName={column?.descriptionOriginColumnName}
                      descriptionOriginResourceUniqueId={
                        column?.descriptionOriginResourceUniqueId
                      }
                      description={column?.description ?? null}
                      fetching={resourceQuery.fetching}
                    />
                  </div>
                </>
              )}
            </>
          );
        }}
      </ColumnPanelTabs>
    </>
  );
};
